// Per-dog training session log. Three flavours: formal (class / trainer-led),
// informal (yard drills, on walks), rehab (vet-prescribed exercises). Each
// entry captures date, type, title, notes, optional file attachments
// (trainer PDFs, exercise sheets), and optionally schedules a review reminder
// 7 days later.
import { useCallback, useEffect, useState } from "react";
import { format, addDays, parseISO, isAfter } from "date-fns";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { SectionTitle } from "@/components/app/Bits";
import { Trash2, Plus, GraduationCap, Home, Dot, BellRing, Paperclip, Stethoscope, FileText, ImageIcon, Bookmark, Zap } from "lucide-react";

const SESSION_TYPES = [
  { value: "formal", label: "Formal", Icon: GraduationCap },
  { value: "informal", label: "Informal", Icon: Home },
  { value: "rehab", label: "Rehab", Icon: Stethoscope },
  { value: "other", label: "Other", Icon: Dot },
];

// Legacy values from before we split formal/informal — keep so old rows
// still render a sensible badge.
const LEGACY_META = {
  class: { label: "Class", Icon: GraduationCap },
  home: { label: "At home", Icon: Home },
};

const TYPE_META = Object.fromEntries(SESSION_TYPES.map((t) => [t.value, t]));

const MAX_ATTACHMENT_BYTES = 2_500_000;     // ~2.5MB per file base64
const MAX_ATTACHMENTS_PER_SESSION = 5;

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = (e) => resolve(e.target.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

export default function TrainingNotesSection({ dogId, dogName }) {
  const today = format(new Date(), "yyyy-MM-dd");
  const [items, setItems] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    session_date: today,
    session_type: "formal",
    title: "",
    notes: "",
    attachments: [],
    remind_next_week: true,
  });

  const load = useCallback(async () => {
    if (!dogId) return;
    setLoading(true);
    try {
      const [notesRes, tmplRes] = await Promise.all([
        api.get(`/dogs/${dogId}/training-notes`),
        api.get(`/dogs/${dogId}/training-templates`),
      ]);
      setItems(notesRes.data.items || []);
      setTemplates(tmplRes.data.items || []);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not load notes");
    } finally {
      setLoading(false);
    }
  }, [dogId]);

  useEffect(() => { load(); }, [load]);

  // Templates -----------------------------------------------------------
  // "Pre-fill" copies the template's fields into the current form so the user
  // can tweak date/notes before saving. "Quick log" fires a POST immediately
  // dated today, no confirmation.
  const applyTemplate = (t) => {
    setForm((f) => ({
      ...f,
      session_type: t.session_type,
      title: t.title,
      notes: t.notes || "",
      attachments: (t.attachments || []).map((a) => ({ ...a })),
    }));
    toast.info(`Loaded "${t.name}" into the form`);
  };

  const quickLogTemplate = async (t) => {
    try {
      await api.post(`/dogs/${dogId}/training-notes`, {
        session_date: today,
        session_type: t.session_type,
        title: t.title,
        notes: t.notes || "",
        attachments: t.attachments || [],
      });
      toast.success(`Logged "${t.name}" for today`);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not log");
    }
  };

  const saveAsTemplate = async () => {
    if (!form.title.trim()) {
      toast.error("Give the session a title first — that becomes the template title.");
      return;
    }
    const name = window.prompt("Name this template (e.g. \"Puppy K homework\")", form.title);
    if (!name) return;
    try {
      await api.post(`/dogs/${dogId}/training-templates`, {
        name: name.trim(),
        session_type: form.session_type,
        title: form.title,
        notes: form.notes,
        attachments: form.attachments,
      });
      toast.success("Template saved");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not save template");
    }
  };

  const deleteTemplate = async (id) => {
    try {
      await api.delete(`/dogs/${dogId}/training-templates/${id}`);
      await load();
    } catch { toast.error("Could not delete"); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return;
    setSaving(true);
    try {
      await api.post(`/dogs/${dogId}/training-notes`, {
        session_date: form.session_date,
        session_type: form.session_type,
        title: form.title,
        notes: form.notes,
        attachments: form.attachments,
      });

      // Optionally schedule a one-time review reminder 7 days after the
      // session date. If the session was back-dated so 7d has already passed,
      // schedule for tomorrow instead so the nudge still shows up.
      if (form.remind_next_week) {
        try {
          const base = parseISO(form.session_date);
          const now = new Date();
          const tomorrow = addDays(now, 1);
          let target = addDays(base, 7);
          if (!isAfter(target, now)) target = tomorrow;
          await api.post(`/reminders`, {
            dog_id: dogId,
            kind: "custom",
            title: `Review: ${form.title.trim()}`,
            notes: form.notes ? `From your training note on ${form.session_date}:\n\n${form.notes}` : "",
            frequency: "once",
            days_of_week: [],
            time_of_day: "09:00",
            tz_offset_minutes: new Date().getTimezoneOffset(),
            run_once_date: format(target, "yyyy-MM-dd"),
            enabled: true,
          });
          toast.success(`Session saved · review reminder set for ${format(target, "MMM d")}`);
        } catch (err) {
          // Note itself saved fine — surface a soft warning only for the reminder step.
          toast.warning(formatApiErrorDetail(err?.response?.data?.detail) || "Note saved, but reminder could not be scheduled");
        }
      } else {
        toast.success("Session saved");
      }

      setForm({
        session_date: today,
        session_type: form.session_type,
        title: "",
        notes: "",
        attachments: [],
        remind_next_week: form.remind_next_week,
      });
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not save note");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/dogs/${dogId}/training-notes/${id}`);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not delete");
    }
  };

  return (
    <div className="space-y-4" data-testid="training-notes-section">
      <SectionTitle overline="Sessions" title="Training notes" />
      <p className="text-sm text-muted-foreground max-w-2xl -mt-2">
        Log what you worked on with <strong>{dogName}</strong> — class notes, drills at home, homework
        from your trainer. Great for spotting patterns and remembering what actually clicked.
      </p>

      <Card className="border-border shadow-none rounded-lg">
        <CardContent className="p-6 space-y-3">
          {templates.length > 0 ? (
            <div data-testid="training-templates-strip">
              <p className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-muted-foreground mb-2">
                <Bookmark size={11} /> Templates ({templates.length})
              </p>
              <ul className="flex flex-wrap gap-1.5">
                {templates.map((t) => (
                  <li
                    key={t.id}
                    className="inline-flex items-stretch bg-secondary/60 rounded-full overflow-hidden border border-border"
                    data-testid={`template-chip-${t.id}`}
                  >
                    <button
                      type="button"
                      onClick={() => applyTemplate(t)}
                      className="text-xs px-3 py-1.5 hover:bg-secondary text-left"
                      title="Pre-fill form"
                      data-testid={`template-apply-${t.id}`}
                    >
                      {t.name}
                    </button>
                    <button
                      type="button"
                      onClick={() => quickLogTemplate(t)}
                      className="text-xs px-2 py-1.5 border-l border-border text-primary hover:bg-primary/10"
                      title="Quick log for today"
                      data-testid={`template-quicklog-${t.id}`}
                    >
                      <Zap size={11} />
                    </button>
                    <button
                      type="button"
                      onClick={() => deleteTemplate(t.id)}
                      className="text-xs px-2 py-1.5 border-l border-border text-muted-foreground hover:text-destructive"
                      title="Delete template"
                      data-testid={`template-delete-${t.id}`}
                    >
                      <Trash2 size={11} />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-4 gap-3" data-testid="training-note-form">
            <div>
              <Label htmlFor="tn-date">Date</Label>
              <Input
                id="tn-date"
                type="date"
                value={form.session_date}
                onChange={(e) => setForm({ ...form, session_date: e.target.value })}
                className="mt-1.5"
                data-testid="training-note-date"
              />
            </div>
            <div>
              <Label htmlFor="tn-type">Type</Label>
              <select
                id="tn-type"
                value={form.session_type}
                onChange={(e) => setForm({ ...form, session_type: e.target.value })}
                className="mt-1.5 w-full border border-input rounded-md bg-card px-3 py-2 text-sm"
                data-testid="training-note-type"
              >
                {SESSION_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div className="md:col-span-2">
              <Label htmlFor="tn-title">Title</Label>
              <Input
                id="tn-title"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder='e.g. "Puppy K week 3", "Recall drills in yard"'
                maxLength={120}
                className="mt-1.5"
                data-testid="training-note-title"
              />
            </div>
            <div className="md:col-span-4">
              <Label htmlFor="tn-notes">Session notes</Label>
              <Textarea
                id="tn-notes"
                rows={4}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="What worked, what didn't, homework, questions to bring to next class…"
                className="mt-1.5 text-sm"
                data-testid="training-note-notes"
              />
            </div>

            <div className="md:col-span-4">
              <Label className="flex items-center gap-1.5 text-xs uppercase tracking-wide text-muted-foreground">
                <Paperclip size={12} /> Attachments (PDFs, exercise sheets, images)
              </Label>
              <div className="mt-1.5 flex flex-wrap items-center gap-2">
                <input
                  type="file"
                  accept="application/pdf,image/*"
                  multiple
                  onChange={async (e) => {
                    const files = Array.from(e.target.files || []);
                    if (!files.length) return;
                    if (form.attachments.length + files.length > MAX_ATTACHMENTS_PER_SESSION) {
                      toast.error(`Up to ${MAX_ATTACHMENTS_PER_SESSION} files per session`);
                      e.target.value = "";
                      return;
                    }
                    const next = [...form.attachments];
                    for (const file of files) {
                      if (file.size > MAX_ATTACHMENT_BYTES) {
                        toast.error(`${file.name} is too big (max 2.5MB)`);
                        continue;
                      }
                      try {
                        const dataUrl = await readFileAsDataUrl(file);
                        next.push({ filename: file.name, mime: file.type || "application/octet-stream", data_url: dataUrl });
                      } catch { toast.error(`Could not read ${file.name}`); }
                    }
                    setForm({ ...form, attachments: next });
                    e.target.value = "";
                  }}
                  className="text-xs"
                  data-testid="training-note-attach-input"
                />
                <span className="text-[11px] text-muted-foreground">
                  {form.attachments.length}/{MAX_ATTACHMENTS_PER_SESSION} files · 2.5MB each
                </span>
              </div>
              {form.attachments.length > 0 ? (
                <ul className="mt-2 flex flex-wrap gap-1.5" data-testid="training-note-attach-preview">
                  {form.attachments.map((a, i) => (
                    <li key={`${a.filename}-${i}`} className="inline-flex items-center gap-1.5 text-xs bg-secondary/60 rounded-full pl-2.5 pr-1 py-1">
                      {a.mime.startsWith("image/") ? <ImageIcon size={11} /> : <FileText size={11} />}
                      <span className="max-w-[180px] truncate">{a.filename}</span>
                      <button
                        type="button"
                        onClick={() => setForm({ ...form, attachments: form.attachments.filter((_, j) => j !== i) })}
                        className="text-muted-foreground hover:text-destructive p-0.5"
                        aria-label={`Remove ${a.filename}`}
                      >
                        <Trash2 size={11} />
                      </button>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
            <div className="md:col-span-4 flex items-center justify-between gap-4 flex-wrap">
              <label
                className="flex items-center gap-2 text-sm text-foreground/85 cursor-pointer select-none"
                data-testid="training-note-remind-label"
              >
                <input
                  type="checkbox"
                  checked={form.remind_next_week}
                  onChange={(e) => setForm({ ...form, remind_next_week: e.target.checked })}
                  className="h-4 w-4 rounded border-input accent-primary"
                  data-testid="training-note-remind-checkbox"
                />
                <BellRing size={13} className="text-primary" />
                <span>Remind me to review this next week (fires 7 days after the session date)</span>
              </label>
              <div className="flex items-center gap-2 ml-auto">
                <Button
                  type="button"
                  variant="outline"
                  onClick={saveAsTemplate}
                  disabled={!form.title.trim()}
                  data-testid="training-note-save-template"
                >
                  <Bookmark size={13} className="mr-1" /> Save as template
                </Button>
                <Button
                  type="submit"
                  disabled={saving || !form.title.trim()}
                  className="bg-primary text-primary-foreground hover:bg-primary/90"
                  data-testid="training-note-submit"
                >
                  <Plus size={14} className="mr-1" /> {saving ? "Saving…" : "Add session"}
                </Button>
              </div>
            </div>
          </form>
        </CardContent>
      </Card>

      <div className="space-y-2" data-testid="training-notes-list">
        {loading ? (
          <p className="text-sm text-muted-foreground">Loading sessions…</p>
        ) : items.length === 0 ? (
          <Card className="border-dashed border-border shadow-none rounded-lg">
            <CardContent className="p-6 text-sm text-muted-foreground text-center">
              No sessions logged yet.
            </CardContent>
          </Card>
        ) : (
          items.map((it) => {
            const meta = TYPE_META[it.session_type] || LEGACY_META[it.session_type] || { label: "Other", Icon: Dot };
            const Icon = meta.Icon;
            return (
              <Card
                key={it.id}
                className="border-border shadow-none rounded-lg"
                data-testid={`training-note-item-${it.id}`}
              >
                <CardContent className="p-4 sm:p-5">
                  <div className="flex items-start gap-3">
                    <div className="h-8 w-8 rounded-md bg-secondary text-secondary-foreground flex items-center justify-center shrink-0">
                      <Icon size={15} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-display font-bold text-base leading-tight">{it.title}</p>
                        <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground bg-secondary/60 rounded px-1.5 py-0.5">
                          {meta.label}
                        </span>
                        <span className="text-[11px] text-muted-foreground">
                          {it.session_date}
                        </span>
                      </div>
                      {it.notes ? (
                        <p className="mt-2 text-sm text-foreground/85 leading-relaxed whitespace-pre-line">{it.notes}</p>
                      ) : null}
                      {Array.isArray(it.attachments) && it.attachments.length > 0 ? (
                        <ul className="mt-2 flex flex-wrap gap-1.5" data-testid={`training-note-attachments-${it.id}`}>
                          {it.attachments.map((a, i) => (
                            <li key={`${a.filename}-${i}`}>
                              <a
                                href={a.data_url}
                                download={a.filename}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1.5 text-xs bg-primary/10 text-primary rounded-full px-2.5 py-1 hover:bg-primary/15"
                              >
                                {a.mime?.startsWith("image/") ? <ImageIcon size={11} /> : <FileText size={11} />}
                                <span className="max-w-[220px] truncate">{a.filename}</span>
                              </a>
                            </li>
                          ))}
                        </ul>
                      ) : null}
                    </div>
                    <button
                      type="button"
                      onClick={() => remove(it.id)}
                      className="text-muted-foreground hover:text-destructive p-1 shrink-0"
                      title="Remove"
                      data-testid={`training-note-remove-${it.id}`}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>
    </div>
  );
}
